import { useQuery } from '@tanstack/react-query'
import { Calendar, Clock } from 'lucide-react'
import { apiClient } from '@/lib/api.ts'
import { EmptyState, LoadingState } from '@/components/ui'

interface CalendarEvent {
  id: string
  subject: string
  start: { dateTime: string }
  end: { dateTime: string }
  location?: { displayName: string }
  isAllDay: boolean
}

export default function CalendarTodayWidget() {
  const { data: events = [], isLoading } = useQuery({
    queryKey: ['calendar-today'],
    queryFn: async () => {
      const today = new Date()
      const start = new Date(today.getFullYear(), today.getMonth(), today.getDate())
      const end = new Date(start.getTime() + 24 * 60 * 60 * 1000)
      const { data } = await apiClient.get<{ events: CalendarEvent[] }>('/calendar/events', {
        params: { from: start.toISOString(), to: end.toISOString() },
      })
      return data.events ?? data
    },
    refetchInterval: 5 * 60 * 1000,
  })

  if (isLoading) {
    return <LoadingState size="sm" label="" />
  }

  const sortedEvents = Array.isArray(events)
    ? [...events].sort((a, b) => new Date(a.start.dateTime).getTime() - new Date(b.start.dateTime).getTime())
    : []

  if (sortedEvents.length === 0) {
    return <EmptyState size="sm" icon={<Calendar />} title="Keine Termine heute" />
  }

  return (
    <div className="space-y-1.5 max-h-[200px] overflow-y-auto">
      {sortedEvents.map((event) => {
        const start = new Date(event.start.dateTime)
        const end = new Date(event.end.dateTime)
        return (
          <div key={event.id} className="flex items-start gap-2 text-body p-1.5 rounded hover:bg-gray-50">
            <div className="flex items-center gap-1 text-gray-500 min-w-[90px]">
              <Clock className="h-3 w-3" />
              {event.isAllDay
                ? 'Ganztägig'
                : `${start.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} - ${end.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-gray-900 truncate">{event.subject}</div>
              {event.location?.displayName && (
                <div className="text-caption text-gray-400 truncate">{event.location.displayName}</div>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}
